import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Upload } from "lucide-react";
import CourseThumbnailUploader from "@/components/admin/CourseThumbnailUploader";

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const CATEGORIES = [
  "Marketing", "AI & Automation", "Creative", "Social Media", "Paid Ads",
  "Development", "Business", "Career", "Personal Growth", "Other",
];

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced", "Beginner to Advanced"];

export default function AdminCourseNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", slug: "", tagline: "", description: "",
    trainer: "Prerna Maheshwari", duration_hours: 10, difficulty: "Beginner",
    thumbnail: "", category: "Marketing", featured: false, published: true,
  });
  const [saving, setSaving] = useState(false);

  function upd(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "title" && !f.slug) next.slug = slugify(v);
      return next;
    });
  }

  async function save() {
    if (!form.title || !form.slug) return toast.error("Title and slug are required");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/courses", form);
      toast.success("Course created");
      nav(`/app/admin/courses/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-5 max-w-3xl" data-testid="admin-course-new">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h2 className="font-display text-3xl font-bold tracking-tight">Add a new course</h2>

      <div className="rounded-3xl bg-card border border-border p-6 space-y-5">
        <CourseThumbnailUploader value={form.thumbnail} onChange={(u) => upd("thumbnail", u)} />

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Course title *</Label>
            <Input value={form.title} onChange={(e) => upd("title", e.target.value)}
              data-testid="new-course-title" className="mt-1.5 rounded-xl h-11" placeholder="e.g. Instagram Growth Masterclass" />
          </div>
          <div>
            <Label>Slug (URL) *</Label>
            <Input value={form.slug} onChange={(e) => upd("slug", slugify(e.target.value))}
              data-testid="new-course-slug" className="mt-1.5 rounded-xl h-11 font-mono text-sm" placeholder="instagram-growth" />
          </div>
        </div>

        <div>
          <Label>Tagline</Label>
          <Input value={form.tagline} onChange={(e) => upd("tagline", e.target.value)}
            data-testid="new-course-tagline" className="mt-1.5 rounded-xl h-11" placeholder="One-line promise for students" />
        </div>

        <div>
          <Label>Description</Label>
          <Textarea value={form.description} onChange={(e) => upd("description", e.target.value)}
            data-testid="new-course-description" className="mt-1.5 rounded-2xl min-h-[120px]" placeholder="Detailed description..." />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Trainer</Label>
            <Input value={form.trainer} onChange={(e) => upd("trainer", e.target.value)} className="mt-1.5 rounded-xl h-11" />
          </div>
          <div>
            <Label>Duration (hours)</Label>
            <Input type="number" value={form.duration_hours} onChange={(e) => upd("duration_hours", parseInt(e.target.value) || 0)} className="mt-1.5 rounded-xl h-11" />
          </div>
          <div>
            <Label>Difficulty</Label>
            <select value={form.difficulty} onChange={(e) => upd("difficulty", e.target.value)}
              className="mt-1.5 w-full h-11 rounded-xl bg-transparent border border-border px-3 text-sm">
              {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <select value={form.category} onChange={(e) => upd("category", e.target.value)}
              data-testid="new-course-category"
              className="mt-1.5 w-full h-11 rounded-xl bg-transparent border border-border px-3 text-sm">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.featured} onCheckedChange={(v) => upd("featured", v)} />
              Featured
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={form.published} onCheckedChange={(v) => upd("published", v)} data-testid="new-course-published" />
              Published
            </label>
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="new-course-save"
          className="rounded-full royal-gradient border-0 text-white btn-lift">
          {saving ? "Creating..." : "Create course"}
        </Button>
      </div>
    </div>
  );
}
